import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  requestExecutarAcaoPlanejamentoEnfermagem,
  requestOpcoesAmbientePreenchimentoEscala,
  requestPlanejamentoProfissionalEnfermagem,
} from "../../services/escalaRequests";
import SearchableSetorSelect from "../Escalas Utils/SearchableSetorSelect";

const DIAS = [
  { id: 1, label: "Seg" },
  { id: 2, label: "Ter" },
  { id: 3, label: "Qua" },
  { id: 4, label: "Qui" },
  { id: 5, label: "Sex" },
  { id: 6, label: "Sáb" },
  { id: 7, label: "Dom" },
];

const clone = (value) => JSON.parse(JSON.stringify(value));

const toIsoDate = (value = "") => String(value || "").slice(0, 10);

// Os valores de apresentação legados (por exemplo, CICLO_12X36) não fazem
// parte do contrato de planejamento. A API aceita o alias temporariamente,
// mas o cliente sempre envia e mantém o valor canônico devolvido por ela.
const toCanonicalPlanningPreset = (value = "") => {
  const normalized = String(value || "")
    .trim()
    .toUpperCase()
    .replace(/^CICLO_/, "");
  if (normalized === "12X36") return "12x36";
  if (normalized === "12X60") return "12x60";
  if (normalized === "24X72" || normalized === "24X120")
    return "PERSONALIZADO";
  if (
    ["SEMANAL", "ROTACAO_PERIODICA", "DATAS_ESPECIFICAS"].includes(normalized)
  ) {
    return normalized;
  }
  return String(value || "").trim();
};

const getTodayIsoDate = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getPlanningVigenciaInicio = (state = {}) =>
  [getTodayIsoDate(), toIsoDate(state?.vinculo_profissional?.data_admissao)]
    .filter(Boolean)
    .sort()
    .at(-1) || getTodayIsoDate();

const getPreviewOccurrences = (preview = {}) => {
  const sources = [
    preview?.criar_ocorrencias,
    preview?.ocorrencias?.criar_ocorrencias,
    preview?.linha_tempo?.depois,
    preview?.jornadas?.depois,
  ];
  const seen = new Set();
  const occurrences =
    sources.find((items) => Array.isArray(items) && items.length) || [];

  return occurrences.filter((item) => {
    const key = [
      item?.jornada_id || item?.id || "",
      item?.data || item?.inicio || item?.data_inicio || "",
      item?.hora_inicio || "",
      item?.hora_fim || item?.fim || "",
    ].join("|");
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const formatOccurrenceDate = (occurrence = {}) => {
  const rawDate = String(
    occurrence?.data || occurrence?.inicio || occurrence?.data_inicio || "",
  ).slice(0, 10);
  const [year, month, day] = rawDate.split("-").map(Number);
  if (!year || !month || !day) return rawDate || "Data não informada";
  const date = new Date(year, month - 1, day);
  const weekday = new Intl.DateTimeFormat("pt-BR", { weekday: "short" })
    .format(date)
    .replace(".", "");
  return `${weekday.charAt(0).toUpperCase()}${weekday.slice(1)} · ${String(day).padStart(2, "0")}/${String(month).padStart(2, "0")}`;
};

const getOccurrenceTime = (occurrence = {}) => {
  const inicio =
    occurrence?.hora_inicio || String(occurrence?.inicio || "").slice(11, 16);
  const fim =
    occurrence?.hora_fim || String(occurrence?.fim || "").slice(11, 16);
  return inicio && fim
    ? `${String(inicio).slice(0, 5)}–${String(fim).slice(0, 5)}`
    : "Horário informado no preview";
};

const formatMinutesAsHours = (value) => {
  const minutes = Number(value);
  if (!Number.isFinite(minutes) || minutes < 0) return "";
  const hours = Math.floor(minutes / 60);
  const remainder = minutes % 60;
  return remainder
    ? `${hours}h${String(remainder).padStart(2, "0")}`
    : `${hours}h`;
};

const formatAlternativeTime = (padrao = {}) => {
  const inicio = String(padrao?.hora_inicio || "").slice(0, 5);
  const fim = String(padrao?.hora_fim || "").slice(0, 5);
  return inicio && fim ? `${inicio}–${fim}` : "";
};

const formatAlternativeDetailValue = (value) => {
  if (typeof value === "string" || typeof value === "number") {
    return String(value);
  }
  // Critérios de ranking e outros metadados podem vir como objeto. Eles são
  // úteis para o backend, mas não podem ser renderizados diretamente no React.
  return "";
};

const getAlternativeDetails = (alternative = {}, preview = {}) => {
  const padrao =
    alternative?.padrao_proposto ||
    alternative?.padrao_desejado ||
    preview?.configuracao?.padroes_origem?.[0] ||
    {};
  const cargaMedia =
    formatMinutesAsHours(
      alternative?.carga_media_semanal_minutos ??
        alternative?.media_semanal_minutos ??
        preview?.carga_media_semanal_minutos,
    ) ||
    (alternative?.carga_media_semanal_horas
      ? `${alternative.carga_media_semanal_horas}h`
      : "");
  const desvio =
    formatMinutesAsHours(
      alternative?.desvio_carga_semanal_minutos ??
        alternative?.desvio_minutos ??
        preview?.desvio_carga_semanal_minutos,
    ) || "";
  const turno = padrao?.turno || alternative?.turno_proposto || "";
  const horario = formatAlternativeTime(padrao);
  const modalidade =
    alternative?.preset_proposto ||
    alternative?.tipo_recorrencia_proposto ||
    padrao?.preset_ciclo ||
    "";

  return [
    ["Modalidade", modalidade ? modalidadeLabel(modalidade) : ""],
    ["Regime", alternative?.regime_proposto || padrao?.regime || ""],
    ["Turno", turno],
    ["Horário", horario],
    ["Carga média semanal", cargaMedia],
    ["Desvio de carga", desvio],
    ["Ranking", alternative?.ranking ?? alternative?.ordem_ranking ?? ""],
  ]
    .map(([label, value]) => [label, formatAlternativeDetailValue(value)])
    .filter(([, value]) => Boolean(value.trim()));
};

const getWarningIssues = (...sources) =>
  Array.from(
    new Map(
      sources
        .flatMap((source) =>
          Array.isArray(source)
            ? source
            : [source?.issues, source?.warnings, source?.avisos].flatMap(
                (items) => (Array.isArray(items) ? items : []),
              ),
        )
        .filter((issue) => issue && !issue.bloqueante)
        .map((issue) => [issue.codigo || issue.warning_code || issue.id, issue])
        .filter(([code]) => Boolean(code)),
    ).values(),
  );

const getWarningCodes = (...sources) =>
  getWarningIssues(...sources).map(
    (issue) => issue.codigo || issue.warning_code || issue.id,
  );

const getPreviewIssueSummaryMessage = (preview = {}) => {
  const issues = [preview?.issues, preview?.preview?.issues].flatMap((items) =>
    Array.isArray(items) ? items : [],
  );
  if (!issues.length) return "";

  return String(
    preview?.resumo?.mensagem ||
      preview?.preview?.resumo?.mensagem ||
      issues.map((issue) => issue?.mensagem || issue?.message).find(Boolean) ||
      "A prévia encontrou impedimentos que precisam ser corrigidos.",
  ).trim();
};

const getApplicationUnavailableMessage = (preview = {}) => {
  if (preview?.aplicacao_disponivel === false)
    return (
      preview?.aplicacao_indisponivel_motivo ||
      "A aplicação desta alternativa não está disponível no momento."
    );
  if (preview?.acoes?.aplicar?.disponivel === false)
    return (
      preview?.acoes?.aplicar?.motivo ||
      "A aplicação desta alternativa não está disponível no momento."
    );
  return "";
};

const sameDays = (first = [], second = []) =>
  [...new Set(first.map(Number))].sort().join(",") ===
  [...new Set(second.map(Number))].sort().join(",");

// A sugestão pode ajustar a jornada para evitar colisões. No salvamento
// silencioso só aceitamos uma alternativa que mantenha exatamente a intenção
// preenchida no formulário; qualquer ajuste continua dependendo de escolha
// explícita da pessoa usuária.
const isAlternativeCompatibleWithForm = (alternative = {}, preview = {}, form = {}) => {
  const padrao =
    alternative?.padrao_proposto ||
    alternative?.padrao_desejado ||
    preview?.comando_normalizado?.configuracao?.padroes_origem?.[0] ||
    preview?.configuracao?.padroes_origem?.[0] ||
    {};
  const preset = toCanonicalPlanningPreset(
    alternative?.preset_proposto ||
      alternative?.tipo_recorrencia_proposto ||
      padrao?.preset_ciclo ||
      padrao?.tipo_recorrencia,
  );
  if (preset !== toCanonicalPlanningPreset(form.preset)) return false;
  if (String(padrao?.hora_inicio || "").slice(0, 5) !== String(form.horaInicio || "").slice(0, 5)) return false;
  if (String(padrao?.hora_fim || "").slice(0, 5) !== String(form.horaFim || "").slice(0, 5)) return false;
  if (preset === "SEMANAL") return sameDays(padrao?.dias_semana, form.diasPadrao);
  if (preset === "ROTACAO_PERIODICA") {
    const semanas = (padrao?.semanas_rotacao || []).map((item) => item?.dias_semana || item);
    return (
      Number(padrao?.periodo_semanas || semanas.length) === Number(form.periodoRotacao) &&
      semanas.length === form.semanasRotacao.length &&
      semanas.every((dias, index) => sameDays(dias, form.semanasRotacao[index]))
    );
  }
  return true;
};

const getAutomaticPlanningConflictMessage = (suggestion = {}, previews = {}, setores = []) => {
  const issues = [
    ...(Array.isArray(suggestion?.issues) ? suggestion.issues : []),
    ...Object.values(previews).flatMap((preview) =>
      Array.isArray(preview?.issues) ? preview.issues : [],
    ),
  ];
  const setorNames = new Map(setores.map((setor) => [String(setor.id), setor.nome || setor.nome_exibicao]));
  const setoresEmConflito = [...new Set(issues.flatMap((issue) =>
    (issue?.alocacoes_origem_candidatas || []).map((origem) => {
      const id = String(origem?.setor_origem_id || origem?.setor_id || "");
      return setorNames.get(id) || (id ? `setor ${id}` : "");
    }),
  ).filter(Boolean))];
  const detalhe = setoresEmConflito.length
    ? ` Setores envolvidos: ${setoresEmConflito.join(", ")}.`
    : "";
  return `A escala escolhida não pode ser aplicada pois gera conflitos com outros setores.${detalhe}`;
};

// A criação de uma versão via planejamento é uma operação automática do
// próprio fluxo. A API exige uma justificativa para auditá-la, mas não há uma
// decisão adicional para a pessoa usuária registrar neste ponto.
const PLANEJAMENTO_JUSTIFICATIVA_AUTOMATICA =
  "Planejamento assistido: criação de nova versão para atuação em outro setor.";

export const addPlanningJustificationToActionBody = (
  entry = {},
  justification = "",
) => {
  const value = String(justification || "").trim();
  if (!value) return entry;
  const hasPlanningProfile =
    entry?.perfil_planejamento && typeof entry.perfil_planejamento === "object";
  return {
    ...entry,
    // O contrato de criar uma versão lê a justificativa no topo da ação
    // HATEOAS. O campo dentro de perfil_planejamento era ignorado quando a
    // entrada não declarava esse perfil, por isso o servidor a recebia vazia.
    justificativa: value,
    ...(hasPlanningProfile
      ? {
          perfil_planejamento: {
            ...entry.perfil_planejamento,
            justificativa: value,
          },
        }
      : {}),
  };
};

const modalidadeLabel = (value) => {
  const normalized = String(value || "")
    .trim()
    .toUpperCase();
  const labels = {
    "12X36": "Plantão 12×36",
    "12X60": "Plantão 12×60",
    "24X72": "Plantão 24×72",
    "24X120": "Plantão 24×120",
    SEMANAL: "Semanal",
    ROTACAO_PERIODICA: "Rotação entre semanas",
    DATAS_ESPECIFICAS: "Datas específicas",
    CICLO_CONTINUO: "Ciclo contínuo",
    PERSONALIZADO: "Ciclo personalizado",
  };
  if (labels[normalized]) return labels[normalized];
  return String(value || "Modalidade da escala")
    .toLowerCase()
    .replace(/_/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
};

const createIdempotencyKey = () =>
  typeof window !== "undefined" && window.crypto?.randomUUID
    ? window.crypto.randomUUID()
    : `planejamento-${Date.now()}-${Math.random().toString(36).slice(2)}`;

const getVinculo = (linha = {}) => {
  const id = Number(
    linha?.vinculo_profissional?.id ||
      linha?.vinculo_profissional_id ||
      linha?.profissional?.vinculo_id ||
      linha?.regime_escala?.vinculo_profissional_id ||
      0,
  );
  if (!id) return null;
  return {
    id,
    nome:
      linha?.profissional?.nome ||
      linha?.vinculo_profissional?.nome ||
      linha?.nome_profissional ||
      `Vínculo ${id}`,
    setorId: Number(linha?.setor?.id || linha?.setor_id || 0) || null,
    setorNome: linha?.setor?.nome || linha?.setor?.nome_exibicao || "",
  };
};

const getModalidadesPlanejaveis = (options = {}) => {
  const catalogo = (options.modalidades_recorrencia || []).filter(
    (item) => item?.planejamento_assistido,
  );
  if (catalogo.length)
    return catalogo
      .map((item) => {
        const preset = toCanonicalPlanningPreset(
          item?.preset_ciclo || item?.codigo,
        );
        return {
          ...item,
          codigo: preset,
          preset_ciclo: preset,
           nome:
             preset === "ROTACAO_PERIODICA"
               ? "12H (segunda à sexta)"
               : preset === "SEMANAL"
                 ? "Semanal"
               : item?.nome,
        };
      })
      .filter((item) => item.preset_ciclo !== "12x36");

  // Compatibilidade com a versão anterior do catálogo: rotação só entra quando
  // preset e tipo forem declarados pelo servidor.
  const presets = new Set(
    (options.presets_ciclo || []).map((item) =>
      toCanonicalPlanningPreset(item?.codigo),
    ),
  );
  const tipos = new Set(
    (options.tipos_recorrencia || []).map((item) => item.codigo),
  );
  return [
    ...["12x60", "SEMANAL"]
      .filter((codigo) => presets.has(codigo))
      .map((codigo) => ({
        codigo,
          nome: codigo === "SEMANAL" ? "Semanal" : codigo,
        preset_ciclo: codigo,
        planejamento_assistido: true,
      })),
    ...(presets.has("ROTACAO_PERIODICA") && tipos.has("ROTACAO_PERIODICA")
      ? [
          {
            codigo: "ROTACAO_PERIODICA",
            nome: "Rotação periódica",
            preset_ciclo: "ROTACAO_PERIODICA",
            planejamento_assistido: true,
          },
        ]
      : []),
  ].map((item) =>
    item?.codigo === "ROTACAO_PERIODICA"
      ? { ...item, nome: "12H (segunda à sexta)" }
      : toCanonicalPlanningPreset(item?.preset_ciclo || item?.codigo) ===
          "SEMANAL"
        ? { ...item, nome: "Semanal" }
        : item,
  ).sort(
    (first, second) =>
      Number(second?.codigo === "ROTACAO_PERIODICA") -
      Number(first?.codigo === "ROTACAO_PERIODICA"),
  );
};

const isSuggestionFormValid = (form) => {
  if (!form.setorOrigemId) return false;
  if (form.preset === "SEMANAL") return form.diasPadrao.length > 0;
  if (form.preset === "ROTACAO_PERIODICA") {
    return (
      form.semanasRotacao.length >= 2 &&
      form.semanasRotacao.every((diasSemana) => diasSemana.length > 0)
    );
  }
  return true;
};

function PlanejamentoProfissionalEnfermagem({
  token,
  contexto,
  linhas = [],
  vinculoProfissionalId = null,
  initialIntent = null,
  hideVinculoSelector = false,
  setoresDestino = [],
  collapsed = false,
  onToggleCollapsed,
  onSuggestionReady,
  onPreviewReady,
  onApplied,
  onReloadRequired,
  confirmRequestVersion = 0,
  onConfirmAvailabilityChange,
  onFormChange,
  compact = false,
  autoGenerate = false,
}) {
  const vinculos = useMemo(() => {
    const unique = new Map();
    linhas
      .map(getVinculo)
      .filter(Boolean)
      .forEach((item) => unique.set(item.id, item));
    return Array.from(unique.values());
  }, [linhas]);
  const setoresExistentes = useMemo(() => {
    const unique = new Map();
    linhas.forEach((linha = {}) => {
      const id = Number(linha?.setor?.id || linha?.setor_id || 0);
      if (id)
        unique.set(
          id,
          linha?.setor?.nome || linha?.setor?.nome_exibicao || `Setor ${id}`,
        );
    });
    return Array.from(unique, ([id, nome]) => ({ id, nome }));
  }, [linhas]);
  const setoresDaTela = setoresDestino.length
    ? setoresDestino
    : setoresExistentes;
  const [vinculoId, setVinculoId] = useState("");
  const [options, setOptions] = useState(null);
  const [state, setState] = useState(null);
  const [suggestion, setSuggestion] = useState(null);
  // Somente uma solicitação explícita de sugestões revela as alternativas.
  // O Salvar pode usar o mesmo mecanismo internamente, sem alterar a tela.
  const [mostrarFluxoManualDeSugestoes, setMostrarFluxoManualDeSugestoes] =
    useState(false);
  const [selectedAlternative, setSelectedAlternative] = useState(null);
  const [alternativaConfirmada, setAlternativaConfirmada] = useState(false);
  const [previewsPorAlternativa, setPreviewsPorAlternativa] = useState({});
  const [avisosAceitosPorAlternativa, setAvisosAceitosPorAlternativa] =
    useState({});
  const [applied, setApplied] = useState(null);
  const [applyKey, setApplyKey] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [stateRefresh, setStateRefresh] = useState(0);
  const [form, setForm] = useState({
    setorOrigemId: "",
    preset: "ROTACAO_PERIODICA",
    horaInicio: "07:00",
    horaFim: "19:00",
    diasPadrao: [1, 2, 3, 4, 5],
    periodoRotacao: "2",
    semanasRotacao: [
      [2, 4],
      [1, 3, 5],
    ],
    cicloPersonalizado: null,
  });
  const estabelecimento = contexto?.estabelecimento;
  const mesReferencia = contexto?.mes_referencia;
  const initialSetorDestinoId = initialIntent?.setorDestinoId;
  const initialSetorOrigemId = initialIntent?.setorOrigemId;
  const initialHoraInicio = initialIntent?.horaInicio;
  const initialHoraFim = initialIntent?.horaFim;
  const initialPreset = initialIntent?.preset;
  const initialDiasPadrao = initialIntent?.diasPadrao;
  const initialPeriodoRotacao = initialIntent?.periodoRotacao;
  const initialSemanasRotacao = initialIntent?.semanasRotacao;
  const initialCicloPersonalizado = initialIntent?.cicloPersonalizado;
  const setorPlanejamentoId = Number(
    form.setorOrigemId || initialSetorDestinoId || 0,
  );

  const selectedVinculo =
    vinculos.find((item) => String(item.id) === String(vinculoId)) ||
    (vinculoProfissionalId
      ? {
          id: Number(vinculoProfissionalId),
          nome: initialIntent?.nome || `Vínculo ${vinculoProfissionalId}`,
        }
      : null);
  const modalidades = useMemo(
    () => getModalidadesPlanejaveis(options || {}),
    [options],
  );
  const modalidadeAtual = modalidades.find(
    (item) =>
      item.preset_ciclo === toCanonicalPlanningPreset(form.preset) ||
      item.codigo === toCanonicalPlanningPreset(form.preset),
  );
  const capability =
    options?.capacidades?.planejamento_inteligente ||
    state?.capacidades?.planejamento_inteligente;
  const suggestionAction = state?.acoes?.sugerir;
  const previewSelecionado = selectedAlternative
    ? previewsPorAlternativa[selectedAlternative.sugestao_id] || null
    : null;
  const applicationUnavailableMessage =
    getApplicationUnavailableMessage(previewSelecionado);
  const avisosDaAlternativaSelecionada = getWarningCodes(
    selectedAlternative,
    previewSelecionado,
  );
  const avisosAceitosDaAlternativa =
    avisosAceitosPorAlternativa[selectedAlternative?.sugestao_id] || [];
  const avisosForamAceitos = avisosDaAlternativaSelecionada.every((codigo) =>
    avisosAceitosDaAlternativa.includes(codigo),
  );
  const canSuggest = Boolean(
    options &&
      capability?.habilitado &&
      suggestionAction?.disponivel,
  );
  const canApply = Boolean(
    previewSelecionado?.acoes?.aplicar?.entrada &&
    previewSelecionado?.acoes?.aplicar?.disponivel !== false,
  );
  const podeConfirmarAlternativa = Boolean(
    selectedAlternative &&
    previewSelecionado &&
    !previewSelecionado?.error &&
    previewSelecionado?.valido !== false &&
    !previewSelecionado?.bloqueado &&
    canApply &&
    avisosForamAceitos &&
    !applicationUnavailableMessage &&
    !loading &&
    !applied,
  );
  const isConfirmationReady = Boolean(
    podeConfirmarAlternativa && alternativaConfirmada,
  );
  const lastConfirmRequestRef = useRef(0);
  const autoGenerateRequestRef = useRef(false);

  useEffect(() => {
    if (vinculoProfissionalId) setVinculoId(String(vinculoProfissionalId));
  }, [vinculoProfissionalId]);

  useEffect(() => {
    if (
      !initialSetorDestinoId &&
      !initialSetorOrigemId &&
      !initialHoraInicio &&
      !initialHoraFim
    )
      return;
    setForm((current) => ({
      ...current,
      // No novo setor, este é o setor de origem da nova alocação. Não é uma
      // partição da escala antiga e, por isso, não enviamos partições por padrão.
      setorOrigemId: String(
        initialSetorDestinoId ||
          (!setoresDestino.length ? initialSetorOrigemId : "") ||
          current.setorOrigemId ||
          "",
      ),
      horaInicio: initialHoraInicio || current.horaInicio,
      horaFim: initialHoraFim || current.horaFim,
      preset: initialPreset
        ? toCanonicalPlanningPreset(initialPreset)
        : current.preset,
      diasPadrao: Array.isArray(initialDiasPadrao)
        ? initialDiasPadrao
        : current.diasPadrao,
      periodoRotacao: initialPeriodoRotacao
        ? String(initialPeriodoRotacao)
        : current.periodoRotacao,
      semanasRotacao: Array.isArray(initialSemanasRotacao)
        ? initialSemanasRotacao
        : current.semanasRotacao,
      cicloPersonalizado:
        initialCicloPersonalizado || current.cicloPersonalizado,
    }));
  }, [
    initialSetorDestinoId,
    initialSetorOrigemId,
    initialHoraInicio,
    initialHoraFim,
    initialPreset,
    initialDiasPadrao,
    initialPeriodoRotacao,
    initialSemanasRotacao,
    initialCicloPersonalizado,
    setoresDestino.length,
  ]);

  useEffect(() => {
    if (!estabelecimento || !setorPlanejamentoId || !mesReferencia) {
      setOptions(null);
      return undefined;
    }
    let active = true;
    setOptions(null);
    setState(null);
    setSuggestion(null);
    setMostrarFluxoManualDeSugestoes(false);
    setSelectedAlternative(null);
    setAlternativaConfirmada(false);
    setPreviewsPorAlternativa({});
    setAvisosAceitosPorAlternativa({});
    setApplied(null);
    setApplyKey("");
    requestOpcoesAmbientePreenchimentoEscala({
      token,
      contexto: {
        estabelecimento,
        setor: setorPlanejamentoId,
        mes_referencia: mesReferencia,
      },
      scope: "enfermagem",
    }).then((response) => {
      if (!active) return;
      if (response?.error) {
        setOptions(null);
        setMessage(
          response.error.message ||
            "Não foi possível consultar as capacidades do planejamento.",
        );
        return;
      }
      setOptions(response);
      const first = getModalidadesPlanejaveis(response)[0];
      if (first)
        setForm((current) => ({
          ...current,
          preset:
            current.preset &&
            getModalidadesPlanejaveis(response).some(
              (item) =>
                item.preset_ciclo ===
                  toCanonicalPlanningPreset(current.preset) ||
                item.codigo === toCanonicalPlanningPreset(current.preset),
            )
              ? toCanonicalPlanningPreset(current.preset)
              : toCanonicalPlanningPreset(first.preset_ciclo),
        }));
    });
    return () => {
      active = false;
    };
  }, [estabelecimento, mesReferencia, setorPlanejamentoId, token]);

  useEffect(() => {
    if (!options || !vinculoId || !estabelecimento || !mesReferencia) {
      setState(null);
      return undefined;
    }
    let active = true;
    setLoading(true);
    requestPlanejamentoProfissionalEnfermagem({
      token,
      estabelecimento,
      mesReferencia,
      vinculoProfissionalId: vinculoId,
    }).then((response) => {
      if (!active) return;
      setLoading(false);
      if (response?.error) {
        setState(null);
        setMessage(
          response.error.message ||
            "Não foi possível consultar o planejamento.",
        );
        return;
      }
      setState(response);
      setSuggestion(null);
      setSelectedAlternative(null);
      setAlternativaConfirmada(false);
      setPreviewsPorAlternativa({});
      setAvisosAceitosPorAlternativa({});
      setApplied(null);
      setApplyKey("");
    });
    return () => {
      active = false;
    };
  }, [estabelecimento, mesReferencia, options, stateRefresh, token, vinculoId]);

  const toggleDay = (field, day, weekIndex = null) => {
    setForm((current) => {
      if (weekIndex != null) {
        const semanas = current.semanasRotacao.map((week, index) =>
          index === weekIndex
            ? week.includes(day)
              ? week.filter((value) => value !== day)
              : [...week, day].sort((a, b) => a - b)
            : week,
        );
        return { ...current, semanasRotacao: semanas };
      }
      return {
        ...current,
        [field]: current[field].includes(day)
          ? current[field].filter((value) => value !== day)
          : [...current[field], day].sort((a, b) => a - b),
      };
    });
  };

  const updateComplementaryTime = (field, value) => {
    // O planejamento pode ser criado com qualquer intervalo. A escolha de uma
    // ponta nao ajusta a outra nem impoe a duracao de um turno padrao.
    setForm((current) => ({ ...current, [field]: value }));
  };

  const setRotationPeriod = (value) => {
    const count = Number(value || 0);
    setForm((current) => ({
      ...current,
      periodoRotacao: value,
      semanasRotacao: count
        ? Array.from(
            { length: count },
            (_, index) => current.semanasRotacao[index] || [],
          )
        : current.semanasRotacao,
    }));
  };

  const requestPreviewForAlternative = async (
    alternative,
    warningsAceitos = [],
  ) => {
    const action = alternative?.acoes?.prever;
    if (!action?.entrada || !action?.disponivel) {
      // Indisponibilidade declarada na própria alternativa não é falha de
      // requisição. Retornamos vazio para desabilitar a confirmação sem criar
      // ou renderizar um alerta de erro local.
      return null;
    }
    const body = addPlanningJustificationToActionBody(
      clone(action.entrada),
      PLANEJAMENTO_JUSTIFICATIVA_AUTOMATICA,
    );
    // A única complementação permitida ao comando devolvido pelo backend.
    body.metadados_planejamento = {
      ...(body.metadados_planejamento || {}),
      warnings_aceitos: warningsAceitos,
    };
    return requestExecutarAcaoPlanejamentoEnfermagem({ token, action, body });
  };

  const carregarPreviewsDasAlternativas = async (alternatives = []) => {
    const responses = await Promise.all(
      alternatives.map(async (alternative) => [
        alternative.sugestao_id,
        // A prévia é somente leitura. Avisos não podem ser aceitos em nome da
        // pessoa usuária: a aceitação acontece apenas após a escolha explícita
        // de uma alternativa.
        await requestPreviewForAlternative(alternative),
      ]),
    );
    return Object.fromEntries(responses);
  };

  const selectAlternative = (alternative) => {
    const previewDaAlternativa =
      previewsPorAlternativa[alternative.sugestao_id];
    setSelectedAlternative(alternative);
    setAlternativaConfirmada(false);
    setApplyKey(createIdempotencyKey());
    setApplied(null);
    setMessage("");

    if (previewDaAlternativa?.error) {
      setMessage(
        previewDaAlternativa.error.message ||
          "Não foi possível preparar a alternativa selecionada.",
      );
      return;
    }

    const resumoIssues = getPreviewIssueSummaryMessage(previewDaAlternativa);
    if (resumoIssues) {
      setMessage(resumoIssues);
      return;
    }

    if (previewDaAlternativa && !previewDaAlternativa.error) {
      onPreviewReady?.({
        setorDestinoId: form.setorOrigemId,
        preset: form.preset,
        horaInicio: form.horaInicio,
        horaFim: form.horaFim,
      });
    }
  };

  const handleAceitarAvisosDaAlternativa = async (checked) => {
    if (!selectedAlternative) return;
    const codigos = getWarningCodes(selectedAlternative, previewSelecionado);
    const sugestaoId = selectedAlternative.sugestao_id;
    setAlternativaConfirmada(false);
    setAvisosAceitosPorAlternativa((current) => ({
      ...current,
      [sugestaoId]: checked ? codigos : [],
    }));

    if (!checked || !codigos.length) return;

    // O preview que libera a aplicação precisa carregar exatamente os avisos
    // aceitos pela pessoa usuária. Não reutilizamos uma prévia anterior.
    setLoading(true);
    const response = await requestPreviewForAlternative(
      selectedAlternative,
      codigos,
    );
    setLoading(false);
    setPreviewsPorAlternativa((current) => ({
      ...current,
      [sugestaoId]: response,
    }));
    if (response?.error) {
      setAvisosAceitosPorAlternativa((current) => ({
        ...current,
        [sugestaoId]: [],
      }));
      setMessage(
        response.error.message ||
          "Não foi possível atualizar a prévia desta alternativa.",
      );
    }
  };

  const requestSuggestion = async ({ aplicarCompativelAutomaticamente = false } = {}) => {
    if (!suggestionAction?.entrada || !selectedVinculo) return;
    if (aplicarCompativelAutomaticamente) {
      setMostrarFluxoManualDeSugestoes(false);
    }
    const justificativa = PLANEJAMENTO_JUSTIFICATIVA_AUTOMATICA;
    const cicloPersonalizado = form.cicloPersonalizado;
    const padrao = {
      preset_ciclo: toCanonicalPlanningPreset(form.preset),
      hora_inicio: form.horaInicio,
      hora_fim: form.horaFim,
      ...(cicloPersonalizado
        ? {
            tipo_recorrencia: "CICLO_CONTINUO",
            preset_ciclo: "PERSONALIZADO",
            trabalho_horas: Number(cicloPersonalizado.trabalhoHoras),
            descanso_horas: Number(cicloPersonalizado.descansoHoras),
            data_ancora: cicloPersonalizado.dataAncora,
          }
        : {}),
      ...(form.preset === "SEMANAL" ? { dias_semana: form.diasPadrao } : {}),
      ...(form.preset === "ROTACAO_PERIODICA"
        ? {
            periodo_semanas: Math.max(
              2,
              Math.min(6, Number(form.periodoRotacao || 2)),
            ),
            semanas_rotacao: form.semanasRotacao.map((dias_semana) => ({
              dias_semana,
            })),
          }
        : {}),
    };
    const entradaSugerir = clone(suggestionAction.entrada);
    const body = addPlanningJustificationToActionBody(
      {
        ...entradaSugerir,
        vinculo_profissional_id: selectedVinculo.id,
        // A janela mensal serve apenas para o cálculo do preview. Uma nova
        // alocação não pode começar retroativamente no primeiro dia dela.
        vigencia_inicio: getPlanningVigenciaInicio(state),
        setor_origem_id: Number(form.setorOrigemId),
        padrao_desejado: padrao,
      },
      justificativa,
    );
    setLoading(true);
    setMessage("");
    const response = await requestExecutarAcaoPlanejamentoEnfermagem({
      token,
      action: suggestionAction,
      body,
    });
    if (response?.error) {
      setLoading(false);
      setMessage(
        response.error.message || "Não foi possível gerar alternativas.",
      );
      return;
    }
    const previews = await carregarPreviewsDasAlternativas(
      response.alternativas || [],
    );
    setSuggestion(response);
    setSelectedAlternative(null);
    setAlternativaConfirmada(false);
    setPreviewsPorAlternativa(previews);
    setAvisosAceitosPorAlternativa({});
    setApplied(null);
    setApplyKey("");
    setLoading(false);

    if (aplicarCompativelAutomaticamente) {
      const alternativaCompativel = (response.alternativas || []).find(
        (alternativa) => {
          const preview = previews[alternativa?.sugestao_id];
          return (
            preview &&
            !preview.error &&
            preview.valido !== false &&
            !preview.bloqueado &&
            preview?.acoes?.aplicar?.entrada &&
            preview?.acoes?.aplicar?.disponivel !== false &&
            isAlternativeCompatibleWithForm(alternativa, preview, form)
          );
        },
      );
      if (!alternativaCompativel) {
        setMessage(getAutomaticPlanningConflictMessage(response, previews, setoresDaTela));
        return;
      }
      const previewCompativel = previews[alternativaCompativel.sugestao_id];
      setSelectedAlternative(alternativaCompativel);
      setAlternativaConfirmada(true);
      setApplyKey(createIdempotencyKey());
      await applyPreview(previewCompativel);
      return;
    }
    onSuggestionReady?.({
      setorDestinoId: form.setorOrigemId,
      preset: form.preset,
      horaInicio: form.horaInicio,
      horaFim: form.horaFim,
    });
  };

  useEffect(() => {
    if (!autoGenerate) {
      autoGenerateRequestRef.current = false;
      return;
    }
    if (
      autoGenerateRequestRef.current ||
      loading ||
      !canSuggest ||
      !isSuggestionFormValid(form)
    ) {
      return;
    }
    autoGenerateRequestRef.current = true;
    setMostrarFluxoManualDeSugestoes(true);
    requestSuggestion();
    // A referência impede novas requisições quando a resposta atualiza a tela.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoGenerate, canSuggest, form, loading]);

  const applyPreview = useCallback(async (previewOverride = null) => {
    const previewParaAplicar = previewOverride || previewSelecionado;
    const action = previewParaAplicar?.acoes?.aplicar;
    if (!action?.entrada || action?.disponivel === false) return;
    const required = action.cabecalhos_obrigatorios || [];
    const etag = action.entrada.preview_etag || previewParaAplicar.preview_etag;
    const stableApplyKey = applyKey || createIdempotencyKey();
    if (!applyKey) setApplyKey(stableApplyKey);
    const headers = {
      ...(required.includes("If-Match") && etag
        ? { "If-Match": String(etag).startsWith('"') ? etag : `"${etag}"` }
        : {}),
      ...(required.includes("Idempotency-Key")
        ? { "Idempotency-Key": stableApplyKey }
        : {}),
    };
    setLoading(true);
    setMessage("");
    const response = await requestExecutarAcaoPlanejamentoEnfermagem({
      token,
      action,
      body: addPlanningJustificationToActionBody(
        clone(action.entrada),
        PLANEJAMENTO_JUSTIFICATIVA_AUTOMATICA,
      ),
      headers,
    });
    setLoading(false);
    if (response?.error) {
      if (response.error.code === "CHAVE_IDEMPOTENCIA_REUTILIZADA") {
        setMessage(
          "A chave desta decisão pertence a outro comando. A aplicação foi interrompida, sem repetição automática.",
        );
        return;
      }
      setMessage(response.error.message || "A aplicação não foi concluída.");
      if (
        response.error.code === "CONFLITO_PERSISTENCIA" ||
        response.error.acao_sugerida === "RECARREGAR_GRADE"
      ) {
        // A prévia não pode ser reaproveitada após um conflito de escrita.
        // Recarregar a origem evita uma nova tentativa sobre estado antigo.
        setSuggestion(null);
        setSelectedAlternative(null);
        setAlternativaConfirmada(false);
        setPreviewsPorAlternativa({});
        setAvisosAceitosPorAlternativa({});
        setApplyKey("");
        setStateRefresh((value) => value + 1);
        onReloadRequired?.(response.error);
        return;
      }
      if (
        [
          "SUGESTAO_PLANEJAMENTO_DESATUALIZADA",
          "PREVIEW_DESATUALIZADO",
        ].includes(response.error.code)
      ) {
        setPreviewsPorAlternativa({});
        setApplyKey("");
        if (response.error.code === "SUGESTAO_PLANEJAMENTO_DESATUALIZADA") {
          setSuggestion(null);
          setSelectedAlternative(null);
          setAlternativaConfirmada(false);
        }
        setStateRefresh((value) => value + 1);
      }
      return;
    }
    setMessage(
      response?.meta_http?.idempotency_replayed
        ? "A aplicação anterior foi recuperada com segurança."
        : "Planejamento aplicado e reconciliado com a resposta do servidor.",
    );
    setApplied(response);
    onApplied?.(response);
  }, [
    applyKey,
    onApplied,
    onReloadRequired,
    previewSelecionado,
    token,
  ]);

  useEffect(() => {
    onConfirmAvailabilityChange?.(isConfirmationReady);
  }, [isConfirmationReady, onConfirmAvailabilityChange]);

  useEffect(() => {
    onFormChange?.({
      setorId: form.setorOrigemId,
      preset: toCanonicalPlanningPreset(form.preset),
      horaInicio: form.horaInicio,
      horaFim: form.horaFim,
      diasPadrao: form.diasPadrao,
      periodoRotacao: form.periodoRotacao,
      semanasRotacao: form.semanasRotacao,
      isValid: isSuggestionFormValid(form),
    });
  }, [form, onFormChange]);

  useEffect(() => {
    if (!confirmRequestVersion || confirmRequestVersion === lastConfirmRequestRef.current)
      return;
    lastConfirmRequestRef.current = confirmRequestVersion;
    if (isConfirmationReady) {
      applyPreview();
      return;
    }
    if (!compact && !loading && isSuggestionFormValid(form)) {
      requestSuggestion({ aplicarCompativelAutomaticamente: true });
    }
    // requestSuggestion é intencionalmente recriada para sempre usar a ação
    // HATEOAS mais recente; a versão da solicitação impede reexecução.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applyPreview, confirmRequestVersion, form, isConfirmationReady, loading]);

  if (!contexto?.estabelecimento) return null;
  if (collapsed && mostrarFluxoManualDeSugestoes && (suggestion || previewSelecionado)) {
    return (
      <section
        className="rounded-md border border-[#b9d6c2] bg-[#f5fbf6] p-3"
        aria-label="Planejamento assistido de enfermagem"
      >
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <p className="text-[13px] font-semibold text-[#225d32]">
              {previewSelecionado
                ? "Alternativa pronta"
                : "Alternativas geradas"}
            </p>
            <p className="text-[12px] text-[#355a3f]">
              {previewSelecionado?.resumo?.mensagem ||
                "Reabra para comparar as alternativas disponíveis."}
            </p>
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onToggleCollapsed}
              className="rounded-md border border-[#225d32] px-3 py-1.5 text-[12px] font-semibold text-[#225d32]"
            >
              Reabrir
            </button>
          </div>
        </div>
      </section>
    );
  }

  const rotationLimits = modalidadeAtual?.limites_periodo_semanas || {};
  return (
    <section
      className="rounded-md border border-[#d7e3ee] bg-white shadow-sm"
      aria-label="Planejamento assistido de enfermagem"
    >
      {!compact && (
        <div className="border-b border-[#d7e3ee] px-5 py-3">
          <p className="text-[13px] text-[#5d6874]">
            Preencha os campos para adicionar em outro setor.
          </p>
        </div>
      )}
      <div className="flex flex-col gap-4 px-5 py-4">
        {!compact && <div
          className={`grid gap-3 ${hideVinculoSelector ? "md:grid-cols-1" : "md:grid-cols-2"}`}
        >
          {!hideVinculoSelector && (
            <label className="text-[13px] font-medium text-[#13335A]">
              Vínculo
              <select
                value={vinculoId}
                onChange={(event) => setVinculoId(event.target.value)}
                className="mt-1 h-9 w-full rounded-md border border-gray-200 bg-white px-2 text-[13px]"
              >
                <option value="">Selecione o profissional</option>
                {vinculos.map((item) => (
                  <option value={item.id} key={item.id}>
                    {item.nome}
                    {item.setorNome ? ` — ${item.setorNome}` : ""}
                  </option>
                ))}
              </select>
            </label>
          )}
          <label className="text-[13px] font-medium text-[#13335A]">
            {setoresDestino.length ? "Novo setor" : "Setor da alocação"}
            <SearchableSetorSelect
              value={form.setorOrigemId}
              options={setoresDaTela}
              onChange={(value) =>
                setForm((current) => ({
                  ...current,
                  setorOrigemId: value,
                }))
              }
              getOptionLabel={(item) => item?.nome || item?.nome_exibicao || ""}
              className="mt-1 h-9 rounded-md border border-gray-200 bg-white px-2 text-[13px] outline-none"
							menuPortal
            />
          </label>
        </div>}

        {state && !compact && (
          <>
            <div className="grid gap-3 md:grid-cols-3">
              <label className="text-[13px] font-medium text-[#13335A]">
                Modalidade da escala
                <select
                  value={form.preset}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      preset: event.target.value,
                    }))
                  }
                  className="mt-1 h-9 w-full rounded-md border border-gray-200 bg-white px-2 text-[13px]"
                >
                  {modalidades.map((item) => (
                    <option
                      value={item.preset_ciclo || item.codigo}
                      key={item.codigo}
                    >
                      {item.nome}
                    </option>
                  ))}
                </select>
              </label>
              <label className="text-[13px] font-medium text-[#13335A]">
                Início
                <input
                  type="time"
                  value={form.horaInicio}
                  onChange={(event) =>
                    updateComplementaryTime("horaInicio", event.target.value)
                  }
                  className="mt-1 h-9 w-full rounded-md border border-gray-200 px-2 text-[13px]"
                />
              </label>
              <label className="text-[13px] font-medium text-[#13335A]">
                Fim
                <input
                  type="time"
                  value={form.horaFim}
                  onChange={(event) =>
                    updateComplementaryTime("horaFim", event.target.value)
                  }
                  className="mt-1 h-9 w-full rounded-md border border-gray-200 px-2 text-[13px]"
                />
              </label>
            </div>
            {form.preset === "SEMANAL" && (
              <DayPicker
                label="Dias que se repetem toda semana"
                selected={form.diasPadrao}
                onToggle={(day) => toggleDay("diasPadrao", day)}
              />
            )}
            {form.preset === "ROTACAO_PERIODICA" && (
              <RotationEditor
                form={form}
                limits={rotationLimits}
                onPeriodChange={setRotationPeriod}
                onToggleDay={toggleDay}
              />
            )}
            <button
              type="button"
              onClick={() => {
                setMostrarFluxoManualDeSugestoes(true);
                requestSuggestion();
              }}
              disabled={
                !canSuggest ||
                loading ||
                !isSuggestionFormValid(form) ||
                (form.preset === "SEMANAL" && !form.diasPadrao.length)
              }
              className="self-start rounded-md bg-[#004A80] px-4 py-2 text-[13px] font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50"
            >
              Gerar alternativas
            </button>
            {!canSuggest && capability && (
              <p className="text-[12px] text-[#895700]">
                {suggestionAction?.motivo ||
                  "A sugestão não está disponível neste estabelecimento ou para este usuário."}
              </p>
            )}
          </>
        )}
        {compact && (
          <button
            type="button"
            onClick={() => requestSuggestion()}
            disabled={!canSuggest || loading || !isSuggestionFormValid(form)}
            className="self-start rounded-md border border-[#004A80] bg-white px-4 py-2 text-[13px] font-semibold text-[#004A80] disabled:cursor-not-allowed disabled:opacity-50"
          >
            Gerar novas alternativas
          </button>
        )}
        {suggestion && mostrarFluxoManualDeSugestoes && (
          <>
            <p className="rounded-md border border-[#d7e3ee] bg-[#f8fafc] px-3 py-2 text-[12px] text-[#36516b]">
              Os dias e horários informados orientam a sugestão. As alternativas
              podem propor outros dias quando isso for necessário para manter a
              escala viável; nessas situações, os impactos serão mostrados para
              sua confirmação.
            </p>
            <Alternatives
              alternatives={suggestion.alternativas || []}
              selected={selectedAlternative}
              previews={previewsPorAlternativa}
              onSelect={selectAlternative}
            />
            {selectedAlternative &&
              avisosDaAlternativaSelecionada.length > 0 && (
                <label className="flex items-start gap-2 rounded-md border border-[#efd9aa] bg-[#fffaf0] px-3 py-2 text-[12px] text-[#6e5314]">
                  <input
                    type="checkbox"
                    checked={avisosForamAceitos}
                    disabled={loading}
                    onChange={(event) =>
                      handleAceitarAvisosDaAlternativa(event.target.checked)
                    }
                    className="mt-0.5"
                  />
                  <span>
                    Li e aceito os avisos desta alternativa. A confirmação será
                    liberada somente após essa aceitação.
                  </span>
                </label>
              )}
            {selectedAlternative && (
              <section
                className={`rounded-md border px-3 py-3 ${alternativaConfirmada ? "border-[#b9d6c2] bg-[#f5fbf6]" : "border-[#b9d6e8] bg-[#f7fbff]"}`}
                aria-live="polite"
              >
                <p className="text-[13px] font-semibold text-[#13335A]">
                  {alternativaConfirmada
                    ? "Alternativa confirmada"
                    : "Alternativa selecionada"}
                </p>
                <p className="mt-1 text-[12px] text-[#36516b]">
                  {alternativaConfirmada
                    ? "Revise uma última vez e use “Confirmar e aplicar” para gravar a escala."
                    : "Confira a prévia, os impactos e os avisos antes de confirmar esta alternativa."}
                </p>
                {applicationUnavailableMessage && (
                  <p className="mt-2 text-[12px] text-[#9a4d00]">
                    {applicationUnavailableMessage}
                  </p>
                )}
                {previewSelecionado?.error && (
                  <p className="mt-2 text-[12px] text-[#A61B29]">
                    {previewSelecionado.error.message ||
                      "Não foi possível preparar a prévia desta alternativa."}
                  </p>
                )}
                {!alternativaConfirmada && (
                  <button
                    type="button"
                    onClick={() => setAlternativaConfirmada(true)}
                    disabled={!podeConfirmarAlternativa}
                    className="mt-3 h-8 rounded-md border border-[#004A80] bg-[#004A80] px-3 text-[12px] font-semibold text-white transition hover:bg-[#0d466c] disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Confirmar alternativa
                  </button>
                )}
              </section>
            )}
          </>
        )}
        {applied && <ApplicationResult result={applied} />}
        {message && (
          <p
            role="alert"
            className="rounded-md bg-[#f7f8fa] p-3 text-[12px] text-[#3f5367]"
          >
            {message}
          </p>
        )}
      </div>
    </section>
  );
}

function DayPicker({ label, selected = [], onToggle }) {
  return (
    <div>
      <p className="text-[12px] font-medium text-[#13335A]">{label}</p>
      <div className="mt-1 flex flex-wrap gap-1">
        {DIAS.map((day) => (
          <button
            type="button"
            key={day.id}
            onClick={() => onToggle(day.id)}
            className={`inline-flex h-[24px] w-[30px] items-center justify-center rounded-md text-[10px] ${selected.includes(day.id) ? "bg-[#004A80] text-white" : "border border-gray-200 bg-white text-[#36516b]"}`}
          >
            {day.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function RotationEditor({ form, limits, onPeriodChange, onToggleDay }) {
  const minimo = Math.max(2, Number(limits.minimo || 2));
  const maximo = Math.min(6, Math.max(minimo, Number(limits.maximo || 6)));
  return (
    <div className="rounded-md border border-[#B8C7D9] px-3 py-3 shadow-md">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="text-[13px] font-medium text-[#13335A]">
            Rotação entre semanas
          </p>
          <p className="text-[12px] text-[#4F647D]">
            Defina os dias de trabalho de cada semana, na ordem em que se
            repetem.
          </p>
        </div>
        <label className="text-[12px] font-medium text-[#13335A]">
          Semanas
          <select
            value={form.periodoRotacao}
            onChange={(event) => onPeriodChange(event.target.value)}
            className="ml-2 h-[32px] rounded-md border border-gray-200 bg-white px-2 text-[12px] font-normal text-[#4F5257] outline-none"
          >
            {Array.from(
              { length: maximo - minimo + 1 },
              (_, index) => minimo + index,
            ).map((value) => (
              <option value={value} key={value}>
                {value} semanas
              </option>
            ))}
          </select>
        </label>
      </div>
      <div className="mt-3 grid gap-3 md:grid-cols-2">
        {form.semanasRotacao.map((dias, index) => (
          <DayPicker
            key={index}
            label={`Semana ${index + 1}`}
            selected={dias}
            onToggle={(day) => onToggleDay("semanasRotacao", day, index)}
          />
        ))}
      </div>
    </div>
  );
}

function Alternatives({ alternatives, selected, previews = {}, onSelect }) {
  if (!alternatives.length)
    return (
      <div className="rounded-md bg-[#fff7e6] p-3 text-[12px] text-[#895700]">
        <strong>Nenhuma alternativa compatível.</strong>
      </div>
    );
  return (
    <div className="grid gap-3 md:grid-cols-2">
      {alternatives.map((alternative) => {
        const preview = previews[alternative.sugestao_id];
        const applicationUnavailableMessage =
          getApplicationUnavailableMessage(preview);
        const occurrences = preview?.error
          ? []
          : getPreviewOccurrences(preview);
        const selectedHere = selected?.sugestao_id === alternative.sugestao_id;
        const detalhes = getAlternativeDetails(alternative, preview);
        return (
          <button
            type="button"
            key={alternative.sugestao_id}
            onClick={() => onSelect(alternative)}
            className={`flex h-full flex-col justify-start rounded-md border p-3 text-left transition ${selectedHere ? "border-[#004A80] ring-1 ring-[#004A80]" : "border-gray-200 hover:border-[#8bb4d3]"}`}
          >
            <div className="flex items-start justify-between gap-2">
              <p className="text-[13px] font-semibold text-[#13335A]">
                Alternativa {alternative.ordem}:{" "}
                {modalidadeLabel(
                  alternative.preset_proposto ||
                    alternative.tipo_recorrencia_proposto ||
                    alternative.padrao_proposto?.preset_ciclo,
                )}
              </p>
              {preview && !preview.error && (
                <span
                  className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold ${applicationUnavailableMessage ? "bg-[#fff3e8] text-[#9a4d00]" : preview.valido && !preview.bloqueado ? "bg-[#e6f5e9] text-[#225d32]" : "bg-[#fff1ee] text-[#A61B29]"}`}
                >
                  {applicationUnavailableMessage
                    ? "Aplicação indisponível"
                    : preview.valido && !preview.bloqueado
                      ? "Pronta"
                      : "Com impedimento"}
                </span>
              )}
            </div>
            {detalhes.length > 0 && (
              <div className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 border-t border-[#e4ecf3] pt-2 text-[11px] text-[#4f647d]">
                {detalhes.map(([label, value]) => (
                  <span key={label}>
                    <strong className="font-semibold text-[#36516b]">
                      {label}:
                    </strong>{" "}
                    {value}
                  </span>
                ))}
              </div>
            )}
            {preview?.error ? (
              <p className="mt-2 text-[12px] text-[#A61B29]">
                {preview.error.message ||
                  "Não foi possível gerar a prévia desta alternativa."}
              </p>
            ) : !preview ? (
              <p className="mt-2 text-[12px] text-[#5d6874]">
                Selecione para visualizar os dias previstos e confirmar esta
                alternativa.
              </p>
            ) : (
              <>
                <p className="mt-3 text-[12px] font-medium text-[#13335A]">
                  Dias previstos ({occurrences.length})
                </p>
                {occurrences.length ? (
                  <div className="mt-1 grid grid-cols-3 gap-1 sm:grid-cols-4">
                    {occurrences.map((occurrence, index) => (
                      <span
                        key={
                          occurrence.jornada_id ||
                          occurrence.id ||
                          `${occurrence.inicio || occurrence.data}-${index}`
                        }
                        className="rounded border border-[#d7e3ee] bg-[#f8fafc] px-1.5 py-1 text-center text-[10px] text-[#36516b]"
                      >
                        <strong className="block font-semibold text-[#13335A]">
                          {formatOccurrenceDate(occurrence)}
                        </strong>
                        {getOccurrenceTime(occurrence)}
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="mt-1 text-[12px] text-[#5d6874]">
                    O preview não retornou dias a criar para esta alternativa.
                  </p>
                )}
              </>
            )}
          </button>
        );
      })}
    </div>
  );
}

function ApplicationResult({ result = {} }) {
  const id = result.alocacao_origem_id || result.alocacao?.id;
  const versao =
    typeof result.versao === "object"
      ? result.versao?.atual || result.versao?.proposta
      : result.versao;
  return (
    <div
      className="rounded-md border border-[#b9d6c2] bg-[#f5fbf6] p-3"
      role="status"
    >
      <p className="text-[13px] font-semibold text-[#225d32]">
        Aplicação concluída
      </p>
      <p className="mt-1 text-[12px] text-[#355a3f]">
        A tela foi reconciliada com a resposta canônica do backend e a grade
        está sendo atualizada.
      </p>
      {id && (
        <p className="mt-1 text-[12px] text-[#355a3f]">
          Alocação: {id}
          {versao ? ` · versão ${versao}` : ""}
        </p>
      )}
    </div>
  );
}

export default PlanejamentoProfissionalEnfermagem;
